<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
    import ReviewsTheme from "@apps/reviews/components/ReviewsTheme/ReviewsTheme.svelte";
    import { REVIEWS_SORTING_TYPE } from "@apps/reviews/constants";
    import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
    import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
    import { filter, lastValueFrom, tap } from "rxjs";
    import { onMount } from "svelte";

    import BaseSkeleton from "@components/skeletons/BaseSkeleton.svelte";
    import EmblaSlider from "@components/Slider/EmblaSlider.svelte";
    import { EmblaSliderApi } from "@components/Slider/types";

    import ReviewsGalleryManager from "../reviews_gallery_widget/ReviewsGalleryManager";
    import ProductReviewsMiniSliderArrows from "./components/ProductReviewsMiniSliderArrows.svelte";
    import ProductReviewsMiniSliderItem from "./components/ProductReviewsMiniSliderItem.svelte";
    import ProductReviewsMiniSliderWidgetReviewsQuery from "./queries/ProductReviewsMiniSliderWidgetReviewsQuery";
    import ProductReviewsMiniSliderWidgetReviewsService from "./services/ProductReviewsMiniSliderWidgetReviewsService";

    export let reviewsQuery: ProductReviewsMiniSliderWidgetReviewsQuery;
    export let reviewsService: ProductReviewsMiniSliderWidgetReviewsService;
    export let reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    export let productIdManager: ProductIdManagerInterface;
    export let lowestRating: number;
    export let onlyPinnedReviews: boolean;
    export let reviewsGalleryManager: ReviewsGalleryManager;
    export let afterWidgetInject: () => void;

    const reviewsBranding$ =
        reviewsStorefrontValuesService.selectReviewsBrandingOptions();

    const reviews$ = reviewsQuery.all$;
    const reviewsStatus$ = reviewsQuery.status$;

    let emblaApi: EmblaSliderApi | null = null;

    const handleNext = (): void => {
        emblaApi?.scrollNext();
    };
    const handlePrev = (): void => {
        emblaApi?.scrollPrev();
    };

    const handleOpenReview = (event: CustomEvent) => {
        const reviewUid = event.detail;
        reviewsGalleryManager.openReviewModal({
            reviews$,
            activeReviewUid: reviewUid,
            widgetName: REVIEWS_WIDGET_NAMES.PRODUCT_REVIEWS_MINI_SLIDER_WIDGET,
        });
    };

    onMount(() => {
        const productIdSubscription = productIdManager.productId$
            .pipe(
                filter((productId): productId is number => productId !== null),
                tap((productId) => {
                    lastValueFrom(
                        reviewsService.getReviews({
                            productId,
                            onlyProductReviews: true,
                            sortingOptions: [
                                REVIEWS_SORTING_TYPE.WITH_PHOTOS,
                                REVIEWS_SORTING_TYPE.MOST_HELPFUL,
                                REVIEWS_SORTING_TYPE.MOST_RECENT,
                            ].join(","),
                            lowestRating: lowestRating,
                            onlyPinnedReviews: onlyPinnedReviews,
                        })
                    );
                })
            )
            .subscribe();

        afterWidgetInject();

        return () => {
            productIdSubscription.unsubscribe();
        };
    });

    $: skeleton = [STATUS.VIRGIN, STATUS.INITIAL_LOADING].includes(
        $reviewsStatus$
    );
</script>

{#if $reviewsBranding$}
    <ReviewsTheme branding={$reviewsBranding$}>
        <div class="gw-rv-product-mini-slider-widget">
            {#if skeleton}
                <div class="gw-rv-product-mini-slider-widget__skeleton">
                    <BaseSkeleton width="64px" height="64px" />

                    <div
                        class="gw-rv-product-mini-slider-widget__skeleton-item"
                    >
                        <div
                            class="gw-rv-product-mini-slider-widget__skeleton-item-row"
                        >
                            <BaseSkeleton width="140px" height="22px" />
                            <BaseSkeleton width="100px" height="22px" />
                        </div>

                        <BaseSkeleton width="100%" height="22px" />
                    </div>
                </div>
            {:else if $reviews$.length > 0}
                <EmblaSlider
                    setApi={(api) => (emblaApi = api)}
                    perPage={1}
                    controls={true}
                    gap={0}
                >
                    <svelte:fragment slot="slides">
                        {#each $reviews$ as review}
                            <ProductReviewsMiniSliderItem
                                {review}
                                on:openReview={handleOpenReview}
                            />
                        {/each}
                    </svelte:fragment>
                    <svelte:fragment slot="arrows" let:hasPrev let:hasNext>
                        <ProductReviewsMiniSliderArrows
                            {hasNext}
                            {hasPrev}
                            on:nextSlide={handleNext}
                            on:prevSlide={handlePrev}
                        />
                    </svelte:fragment>
                </EmblaSlider>
            {/if}
        </div>
    </ReviewsTheme>
{/if}

<style lang="postcss" global>
    @import "../../../../themes/default_v2/default.css";
    @import "../../../../themes/globals.css";

    .gw-rv-product-mini-slider-widget {
        display: flex;
        align-items: center;
        border-radius: var(--gw-border-radius-200);
        background: var(--gw-bg-fill-secondary, #fbfcfe);
        overflow: hidden;

        &__skeleton {
            display: flex;
            justify-content: center;
            gap: 0.5em;
            align-items: center;
            padding: 0.5em 2em;
        }

        &__skeleton-item {
            display: flex;
            gap: 0.5em;
            flex-direction: column;

            &-row {
                display: flex;
                justify-content: space-between;
                gap: 0.25em;
            }
        }
    }
</style>
